import posthog from "posthog-js";
import { useEffect, useLayoutEffect, useState } from "react";
import { MarketingHeader } from "../MarketingHeader/MarketingHeader";
import { Outlet, useLocation } from "react-router-dom";
import { Meta } from "../Meta/Meta";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Image, Pane } from "evergreen-ui";
import { backgroundColor } from "../../lib/useBackgroundColor";
import { useStoreData } from "../../lib/store/store";
import { apiReq } from "../../lib/apiReq";
import { UTM_KEYS } from "@greenflagdate/shared";
import { Link } from "react-router-dom";
import { useGFTheme } from "../../lib/themes";
import instagram from "./images/instagram.png";

import styles from "./Page.module.css";

dayjs.extend(localizedFormat);

export function Page() {
  const location = useLocation();
  const [bgColor, setBgColor] = useState(backgroundColor.val);
  const { safeArea } = useStoreData();
  const { colors } = useGFTheme();
  const { hash } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const searchParams = new URLSearchParams(location.search);
    for (const [key, value] of searchParams) {
      const lowerKey = key.toLowerCase();
      if (UTM_KEYS.includes(lowerKey)) {
        if (value) {
          sessionStorage.setItem(lowerKey, value);
          apiReq.headers[lowerKey] = value;
        }
      }
    }
    posthog.capture("$pageview");
    // @ts-expect-error defined by script code in head
    if (window.fbq) {
      // @ts-expect-error defined by script code in head
      window.fbq("track", "PageView");
    }
  }, [location]);
  useEffect(() => {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
      api_host: "https://app.posthog.com",
    });
  }, []);
  useEffect(() => {
    setBgColor(backgroundColor.val);
    function bgColorChange(event: Event) {
      setBgColor((event as CustomEvent<{ color: string }>).detail.color);
    }
    backgroundColor.addEventListener("update", bgColorChange);
    return () => {
      backgroundColor.removeEventListener("update", bgColorChange);
    };
  }, []);

  useLayoutEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const el = document.getElementById(id);
    setTimeout(() => el?.scrollIntoView({ behavior: "smooth" }), 20);
  }, [hash]);

  return (
    <Pane
      paddingTop={safeArea.top}
      paddingRight={safeArea.right}
      paddingBottom={safeArea.bottom}
      paddingLeft={safeArea.left}
      is="main"
      backgroundColor={bgColor ? bgColor : undefined}
    >
      <Meta>
        <meta name="description" content="Figure out your dating style" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="" />
        <meta property="og:type" content="" />
        <meta property="og:url" content="" />
        <meta property="og:image" content="" />

        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        <meta name="theme-color" content="#ffffff" />
        <script>
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${import.meta.env.VITE_META_PIXEL_ID}');
            fbq('track', 'PageView');
          `}
        </script>
        <noscript>
          {`<img height="1" width="1" style="display:none"
              src="https://www.facebook.com/tr?id=${
                import.meta.env.VITE_META_PIXEL_ID
              }&ev=PageView&noscript=1"
            />`}
        </noscript>
      </Meta>
      <Pane display="flex" flexDirection="column" minHeight="100vh">
        <MarketingHeader />
        <Pane flex={1} display="flex" flexDirection="column">
          <Outlet />
        </Pane>
        <Pane>
          <Pane display="flex" className={styles.footerLinks}>
            <Pane>
              <Link style={{ display: "block", padding: "0.75rem 0" }} to="/">
                ABOUT
              </Link>
              <Link
                style={{ display: "block", padding: "0.75rem 0" }}
                to="/contact"
              >
                CONTACT
              </Link>
            </Pane>
            <Pane>
              <Link
                style={{ display: "block", padding: "0.75rem 0" }}
                to="/policy/privacy"
              >
                PRIVACY POLICY
              </Link>
              <Link
                style={{ display: "block", padding: "0.75rem 0" }}
                to="/policy/refunds"
              >
                REFUND POLICY
              </Link>
            </Pane>
          </Pane>
          <Pane
            backgroundColor={colors.greenLight}
            borderTop={`1px solid ${colors.greenDark}`}
            paddingY="2rem"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Pane
              is="a"
              href="https://www.instagram.com/greenflagdate/"
              display="block"
            >
              <Image src={instagram} width="1.25rem" />
            </Pane>
            <Pane>&copy; Green Flag Date LLC 2024-{dayjs().year()}</Pane>
          </Pane>
        </Pane>
      </Pane>
    </Pane>
  );
}
